import store from '@/store/store';
import moment from "moment";
import { translateError } from './errorTranslator';
import { InputType } from '@/models/enums';

export const errorStatusCode = (error) => {
    let stringConstructor = 'string'.constructor;
    if (error.constructor === stringConstructor) {
        try {
            error = JSON.parse(error)
        } catch (e) {
            return 999;
        }
    }

    return error.response
        ? (error.response.status
            ? error.response.status
            : (error.response.statusCode
                ? error.response.statusCode
                : 400)
        )
        : 999;
};

export const responseStatusCode = (response) => {
    let stringConstructor = 'string'.constructor;
    if (response.constructor === stringConstructor) {
        try {
            response = JSON.parse(response)
        } catch (e) {
            return 200;
        }
    }

    if (response.data && response.data.statusCode) {
        return response.data.statusCode;
    }

    if (response.request) {
        return response.request.status;
    }

    return 200;
};

export const errorText = (error) => {
    if (!error) {
        return 'unknown error';
    }

    let stringConstructor = 'string'.constructor;
    if (error.constructor === stringConstructor) {
        try {
            error = JSON.parse(error)
        } catch (e) {
            return 'unknown error';
        }
    }

    if (error.response) {
        if (error.response.data && error.response.data.message) {
            return error.response.data.message;
        }

        if (error.response.statusText) {
            return error.response.statusText;
        }
    }

    if (error.message) {
        return error.message;
    }

    return 'unknown error';
};

export const errorColor = (error) => {
    if (!error) {
        return 'danger';
    }

    let stringConstructor = 'string'.constructor;
    if (error.constructor === stringConstructor) {
        try {
            error = JSON.parse(error)
        } catch (e) {
            return 'danger';
        }
    }

    if (error.response) {
        if (error.response.data && error.response.data.color) {
            return error.response.data.color;
        }

        if (error.response.color) {
            return error.response.color;
        }
    }

    if (error.color) {
        return error.color;
    }

    return 'danger'
};

export const notificationColor = (val) => {
    let statusCode = Number(val);
    if (isNaN(statusCode)) {
        return 'danger';
    }

    if (statusCode >= 200 && statusCode < 400) {
        return 'success';
    }

    if (statusCode === 403) {
        return 'warning';
    }

    return 'danger';
};

export const kendoDataSourceErrorText = (e) => {
    // i18n.t('');

    if (e.xhr && e.xhr.responseText) {
        return e.xhr.responseText;
    }

    if (e.xhr) {
        return e.status + ' ' + e.xhr.status + '</br>' + (e.xhr.responseJSON && e.xhr.responseJSON.message ? e.xhr.responseJSON.message : '');
    }

    return e.status;
};

export const responseMessage = (response) => {
    if (!response) {
        return '';
    }

    let stringConstructor = 'string'.constructor;
    if (response.constructor === stringConstructor) {
        try {
            response = JSON.parse(response)
        } catch (e) {
            return '';
        }
    }

    if (response.data && response.data.message) {
        return response.data.message;
    }

    if (response.request && response.request.statusText) {
        return response.request.statusText;
    }

    if (response.message) {
        return response.message;
    }

    return '';
};

export const castIdRouteParams = (route) => {
    const id = Number.parseInt(route.params.id, 10);
    if (Number.isNaN(id)) {
        return 0;
    }
    return { id }
};

export const castRelatedIdRouteParams = (route) => {
    const relatedId = Number.parseInt(route.params.relatedId, 10);
    if (Number.isNaN(relatedId)) {
        return 0;
    }
    return { relatedId }
};

export const castStringIdRouteParams = (route) => {
    const id = route.params.id;
    return { id }
};

export const convertFromUtcToLocal = (value) => {
    if (!value) {
        return value;
    }

    let date;
    const dateUtc = new Date(value);

    if (dateUtc.getTimezoneOffset() > 0 || dateUtc.getTimezoneOffset() < 0) {
        date = new Date(
            Date.UTC(
                dateUtc.getFullYear(),
                dateUtc.getMonth(),
                dateUtc.getDate(),
                dateUtc.getHours(),
                dateUtc.getMinutes(),
                dateUtc.getSeconds()
            )
        );
    } else {
        date = dateUtc;
    }

    return date;
}

export const updateFieldTemplateObject = (source, target) => {
    if (!source || !target) {
        return source;
    }

    source.name = target.name || source.name;
    source.nameEn = target.nameEn || source.nameEn;
    source.label = target.label || source.label;
    source.labelEn = target.labelEn || source.labelEn;
    source.inputType = target.inputType || source.inputType; // text / number for Input
    source.size = target.size || source.size;

    // Constraints
    source.isRequired = target.isRequired || source.isRequired;
    source.isUnique = target.isUnique || source.isUnique;
    source.isReadOnly = target.isReadOnly || source.isReadOnly;
    source.regexValidation = target.regexValidation || source.regexValidation;

    source.numberType = target.numberType || source.numberType;

    source.intMinValue = target.intMinValue || source.intMinValue;
    source.intMaxValue = target.intMaxValue || source.intMaxValue;
    source.intDefaultValue = target.intDefaultValue || source.intDefaultValue;

    source.decimalMinValue = target.decimalMinValue || source.decimalMinValue;
    source.decimalMaxValue = target.decimalMaxValue || source.decimalMaxValue;
    source.decimalDefaultValue = target.decimalDefaultValue || source.decimalDefaultValue;
    source.decimalPrecision = target.decimalPrecision || source.decimalPrecision;

    source.numberFormat = target.numberFormat || source.numberFormat;
    source.numberType = target.numberType || source.numberType;

    source.textMinLength = target.textMinLength || source.textMinLength;
    source.textMaxLength = target.textMaxLength || source.textMaxLength;
    source.textDefaultValue = target.textDefaultValue || source.textDefaultValue;
    source.textAllowNullOrEmpty = target.textAllowNullOrEmpty || source.textAllowNullOrEmpty;

    source.datetimeMinValue = target.datetimeMinValue || source.datetimeMinValue;
    source.datetimeMaxValue = target.datetimeMaxValue || source.datetimeMaxValue;
    source.datetimeDefaultValue = target.datetimeDefaultValue || source.datetimeDefaultValue;
    source.datetimeFormat = target.datetimeFormat || source.datetimeFormat;

    source.booleanDefaultValue = target.booleanDefaultValue || source.booleanDefaultValue;

    // Dropdown
    source.isMultiDropdown = target.isMultiDropdown;
    source.dropdownIsCity = target.dropdownIsCity;
    source.dropdownType = target.dropdownType;
    source.dropdownId = target.dropdownId;
    source.dropdownDisplayMember = target.dropdownDisplayMember;

    // model.fieldValue = new DynamicRegisterFieldValueModel(target.fieldValue || {});

    // Locked for edit
    // model.locked = obj.locked || false;
    source.fieldCode = target.fieldCode;
    // this.isTemplate = obj.isTemplate || false;
};

export const isNumeric = (n) => {
    return !isNaN(parseFloat(n)) && isFinite(n);
};

export const getBoolean = (str) => {
    if ("true".startsWith(str)) {
        return true;
    } else if ("false".startsWith(str)) {
        return false;
    } else {
        return null;
    }
};

export const getMassFilterForGrid = (grid, val, excludedColumns) => {
    // grid - Kendo ui grid jquery obj from kendoWidget();
    var columns = grid.columns;
    if (!excludedColumns) {
        excludedColumns = [];
    }
    var filter = { logic: 'or', filters: [] };
    if (!val) {
        return filter;
    }

    val = val.trim();

    columns.forEach(function (x) {
        if (x.field && excludedColumns.includes(x.field) === false) {
            if (!grid.dataSource.options.schema.model.fields[x.field]) {
                return;
            }

            const type = grid.dataSource.options.schema.model.fields[x.field].type;
            if (type === 'string') {
                filter.filters.push({
                    field: x.field,
                    operator: 'contains',
                    value: val
                });
            } else if (type === 'number') {
                if (isNumeric(val)) {
                    filter.filters.push({
                        field: x.field,
                        operator: 'eq',
                        value: val
                    });
                }
            } else if (type === 'date') {
                const data = grid.dataSource.data();
                for (let i = 0; i < data.length; i++) {
                    const dateStr = kendo.format(x.format, data[i][x.field]);
                    // change to includes() if you wish to filter that way https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/includes
                    if (dateStr.startsWith(val)) {
                        filter.filters.push({
                            field: x.field,
                            operator: 'eq',
                            value: data[i][x.field]
                        });
                    }
                }
            } else if (type === 'boolean' && getBoolean(val) !== null) {
                var bool = getBoolean(val);
                filter.filters.push({
                    field: x.field,
                    operator: 'eq',
                    value: bool
                });
            }
        }
    });

    return filter;
};

export const dateRangesDropdownSource = () => {
    return [
        {
            text: 'Днес',
            textEn: 'Today',
            value: 0
        },
        {
            text: 'Тази седмица',
            textEn: 'This week',
            value: 1
        },
        {
            text: 'Миналата седмица',
            textEn: 'Last week',
            value: 2
        },
        // {
        //    text: 'Този месец',
        //    textEn: 'This month',
        //    value: 2
        // },
        // {
        //    text: 'Тази година',
        //    textEn: 'This year',
        //    value: 3
        // }
    ];
};

export const calculateDateRange = (value, isUtc) => {
    let today;
    if (value === 0) {
        // Today
        if (isUtc) {
            today = new Date(new Date(value).toUTCString());
            return new Date(
                today.getFullYear(),
                today.getMonth(),
                today.getDate());
        }

        today = new Date();
        return new Date(
            today.getFullYear(),
            today.getMonth(),
            today.getDate());
    }

    if (value === 1) {
        // This week
        if (isUtc) {
            let todayUtc = new Date(new Date(value).toUTCString());
            today = new Date(
                todayUtc.getFullYear(),
                todayUtc.getMonth(),
                todayUtc.getDate());
        } else {
            let time = new Date();
            today = new Date(
                time.getFullYear(),
                time.getMonth(),
                time.getDate());
        }

        let diff = today.getDate() - today.getDay() + (today.getDay() === 0 ? -6 : 1);
        let startTime = new Date(today.setDate(diff));
        let endTime = new Date(startTime).addDays(7);
        return { startTime, endTime }
    }

    if (value === 2) {
        // Last week
        if (isUtc) {
            let todayUtc = new Date(new Date(value).toUTCString());
            today = new Date(
                todayUtc.getFullYear(),
                todayUtc.getMonth(),
                todayUtc.getDate());
        } else {
            let time = new Date();
            today = new Date(
                time.getFullYear(),
                time.getMonth(),
                time.getDate());
        }

        let diff = today.getDate() - today.getDay() + (today.getDay() === 0 ? -6 : 1);
        let thisWeekStart = new Date(today.setDate(diff));
        let startTime = new Date(thisWeekStart).addDays(-7);
        let endTime = new Date(startTime).addDays(7);
        return { startTime, endTime }
    }
};

export const haveClaimReadRights = (claim) => {
    if (!claim) {
        return false;
    }

    if (claim.read) {
        return true;
    }

    return false;
}

export const haveClaimCreateOrEditRights = (claim) => {
    if (!claim) {
        return false;
    }

    if (claim.create || claim.update) {
        return true;
    }

    return false;
}

export const haveClaimDeleteRights = (claim) => {
    if (!claim) {
        return false;
    }

    if (claim.delete) {
        return true;
    }

    return false;
}

export const notify = (title, text, color, time) => {
    this.$vs.notify({
        title: title,
        text: text,
        color: color,
        time: time
    });
}

export const roundToSecondDigit = (number) => {
    return Math.round(number * 100) / 100;
}

export const getGridSelectedRows = (grid) => {
    if (!grid) {
        return null;
    }

    let selectedItems = [];
    const rows = grid.select();

    if (rows && rows.length > 0) {
        $(rows).each(function (idx, item) {
            const dataItem = grid.dataItem(item)
            selectedItems.push(dataItem);
        });
    }

    return selectedItems;
}

export const getGridSelectedRowsIds = (grid) => {
    let selectedIds = [];
    const selectedItems = getGridSelectedRows(grid);
    if (!selectedItems) {
        return selectedIds;
    }

    selectedItems.forEach(item => {
        if (selectedIds.includes(item.id) === false) {
            selectedIds.push(item.id)
        }
    });

    return selectedIds;
}

export const getGridSelectedRowsByProperty = (grid, propName) => {
    if (!propName) {
        return null;
    }

    let selectedItems = [];
    let selectedIds = [];
    const selectedRows = getGridSelectedRows(grid);

    if (!selectedRows) {
        return selectedItems;
    }

    selectedRows.forEach(item => {
        if (selectedIds.includes(item.id) === false) {
            selectedIds.push(item.id);
            selectedItems.push(item[propName]);
        }
    });

    return selectedItems;
}

export const getUid = () => {
    return Math.floor((Math.random()) * 0x10000).toString(16);
}

export const kendoVirtualizationValueMapper = (dataSource, valueField = 'id') => {
    return (options) => {
        if (options.value) {
            const index = dataSource.findIndex(item => item[valueField] === options.value);
            options.success(index);
        } else {
            options.success(options.value);
        }
    };
}

export const toBase64 = (obj) => {
    return btoa(unescape(encodeURIComponent(obj)));
};

export const sizeClassObject = (panelSize) => {
    return 'col-md-' + panelSize + ' col-sm-' + panelSize;
}

export const gridTextCellTemplate = (dataItem, propName, skipGridShowEllipsis) => {
    if (!dataItem || !dataItem.hasOwnProperty(propName)) {
        return '';
    }

    const gridShowEllipsis = !!skipGridShowEllipsis === false && store.getters.gridShowEllipsis;

    return '<span ' + (gridShowEllipsis ? 'class="expandable-text"' : '') + ' title="' + (dataItem[propName] ? dataItem[propName].replace(/["]/g, "'").replace('"', '\'') : '') + '">' + (dataItem[propName] ? dataItem[propName] : "") + '</span>';
}

export const toggleEllpsisClass = (userSettings) => {
    const ellipsisShow = 'expandable-text';
    const ellipsisHide = 'expandable-text-hidden';
    const removeEllipsis = !!userSettings.removeEllipsis;
    console.log(removeEllipsis);

    if (removeEllipsis === true) {
        // Change expandable-text to expandable-text-hidden
        const els = document.getElementsByClassName(ellipsisShow);
        if (els) {
            els.forEach((el) => {
                if (el.classList.contains(ellipsisShow)) el.classList.remove(ellipsisShow);
                if (!el.classList.contains(ellipsisHide)) el.classList.add(ellipsisHide);
            });
        }
    } else {
        // Change expandable-text to expandable-text-hidden
        const els = document.getElementsByClassName(ellipsisHide);
        if (els) {
            els.forEach((el) => {
                if (el.classList.contains(ellipsisHide)) el.classList.remove(ellipsisHide);
                if (!el.classList.contains(ellipsisShow)) el.classList.add(ellipsisShow);
            });
        }
    }
}

export const filter = (item, search) => {
    if (!item) return true;
    if (!search) return true;

    const entries = Object.entries(item);
    if (entries.length === 0) {
        return !!item.toLocaleString().toLowerCase().includes(search.toLocaleString().toLowerCase());
    } else {
        // eslint-disable-next-line no-unused-vars
        for (const [key, value] of entries) {
            if (value) {
                let searched = value;
                if (isIsoDate(searched)) {
                    searched = moment.utc(searched).format('DD.MM.YYYY');
                }

                if (!!searched.toLocaleString().toLowerCase().includes(search.toLocaleString().toLowerCase()) === true) return true;
            }
        }

        return false;
    }
}

export const isIsoDate = (str) => {
    if (!/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z/.test(str)
     && !/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z/.test(str)) return false;
    const d = new Date(str);
    return d instanceof Date && !isNaN(d); // valid date
}

export const isInt = (n) => {
    return Number(n) === n && n % 1 === 0;
}

export const isFloat = (n) => {
    return Number(n) === n && n % 1 !== 0;
}

export const blobToFile = (blob, fileName) => {
    // A Blob() is almost a File() - it's just missing the two properties below which we will add
    blob.lastModifiedDate = new Date();
    blob.name = fileName;
  return blob;
}


export const fillProfileDataToDynamicForm = (model, data) => {
    if (!model || !data) {
        return null;
    }

    switch (model.fieldCode) {
        case 'Person_FirstName_Form':
            // Полето трябва да е текстово. Събира инфомация за името на попълващия.
            if (data.firstName && model.fieldType === 'Input' && (model.inputType === InputType.Text || model.inputType === InputType.TextArea)) {
                model.fieldValue.textValue = data.firstName;
                model.fieldValue.stringValue = data.firstName;

                return data.firstName;
            }

            return null;
        case 'Person_FirstNameEn_Form':
            // Полето трябва да е текстово. Събира инфомация за името на латиница на попълващия.
            if (data.firstNameEn && model.fieldType === 'Input' && (model.inputType === InputType.Text || model.inputType === InputType.TextArea)) {
                model.fieldValue.textValue = data.firstNameEn;
                model.fieldValue.stringValue = data.firstNameEn;

                return data.firstNameEn;
            }
            return null;
        case 'Person_MiddleName_Form':
            // Полето трябва да е текстово. Събира инфомация за презимето на попълващия.
            if (data.middleName && model.fieldType === 'Input' && (model.inputType === InputType.Text || model.inputType === InputType.TextArea)) {
                model.fieldValue.textValue = data.middleName;
                model.fieldValue.stringValue = data.middleName;

                return data.middleName;
            }
            return null;
        case 'Person_MiddleNameEn_Form':
            // Полето трябва да е текстово. Събира инфомация за презимето на латиница на попълващия.
            if (data.middleNameEn && model.fieldType === 'Input' && (model.inputType === InputType.Text || model.inputType === InputType.TextArea)) {
                model.fieldValue.textValue = data.middleNameEn;
                model.fieldValue.stringValue = data.middleNameEn;

                return data.middleNameEn;
            }
            return null;
        case 'Person_LastName_Form':
            // Полето трябва да е текстово. Събира инфомация за фамилията на попълващия.
            if (data.lastName && model.fieldType === 'Input' && (model.inputType === InputType.Text || model.inputType === InputType.TextArea)) {
                model.fieldValue.textValue = data.lastName;
                model.fieldValue.stringValue = data.lastName;

                return data.lastName;
            }
            return null;
        case 'Person_LastNameEn_Form':
            // Полето трябва да е текстово. Събира инфомация за фамилията на латиница на попълващия.
            if (data.lastNameEn && model.fieldType === 'Input' && (model.inputType === InputType.Text || model.inputType === InputType.TextArea)) {
                model.fieldValue.textValue = data.lastNameEn;
                model.fieldValue.stringValue = data.lastNameEn;

                return data.lastNameEn;
            }
            return null;
        case 'Person_CityOfCurrentAddress_Form':
            // Полето трябва да е текстово. Събира инфомация за "Град (село) по настоящ адрес" на попълващия.
            if (data.addressSettlementName && model.fieldType === 'Input' && (model.inputType === InputType.Text || model.inputType === InputType.TextArea)) {
                model.fieldValue.textValue = data.addressSettlementName;
                model.fieldValue.stringValue = data.addressSettlementName;

                return data.address.addressSettlementName;
            }
            return null;
        case 'Person_CurrentSettlement_Form':
            // Полето трябва да е dropdown от тип "Номенклатура" с тип "Населени места". Събира инфомация за "Настоящо населено място" на попълващия.
            if (data.address && data.address.settlementId && model.fieldType === 'Dropdown') {
                model.fieldValue.intValue = data.address.settlementId;

                return data.address.settlementId;
            }
            return null;
        case 'Person_EducationDegree_Form':
            // Полето трябва да е dropdown от тип "Номенклатура". Събира инфомация за "Образование" на попълващия.
            if (data.educationDegreeId && model.fieldType === 'Dropdown') {
                model.fieldValue.intValue = data.educationDegreeId;

                return data.educationDegreeId;
            }
            return null;
        case 'Person_Email_Form':
            // Полето трябва да е текстово. Събира инфомация за ел.поща на попълващия.
            if (data.email && model.fieldType === 'Input' && (model.inputType === InputType.Text || model.inputType === InputType.TextArea)) {
                model.fieldValue.textValue = data.email;
                model.fieldValue.stringValue = data.email;

                return data.email;
            }
            return null;
        case 'Person_Employment_Form':
            // Полето трябва да е dropdown от тип "Номенклатура". Събира инфомация за формата на заетост на попълващия.
            return null;
        case 'Person_GenderType_Form':
            // Полето трябва да е dropdown от тип "Номенклатура". Събира инфомация за пола на попълващия.
            if (data.genderTypeId && model.fieldType === 'Dropdown') {
                model.fieldValue.intValue = data.genderTypeId;

                return data.genderTypeId;
            }
            return null;
        case 'Person_IdCardDateOfIsssue_Form':
            // Полето трябва да е дата. Събира инфомация за датата на издаване на ЛК на попълващия.
            if (data.idCardDateOfIssue && model.fieldType === 'Datepicker') {
                model.fieldValue.datetimeValue = data.idCardDateOfIssue;
                model.fieldValue.stringValue = data.idCardDateOfIssue;

                return data.idCardDateOfIssue;
            }
            return null;
        case 'Person_IdCardIssuedBy_Form':
            // Полето трябва да е текстово. Събира инфомация за издателя на ЛК на попълващия.
            if (data.idCardIssuedBy && model.fieldType === 'Input' && (model.inputType === InputType.Text || model.inputType === InputType.TextArea)) {
                model.fieldValue.textValue = data.idCardIssuedBy;
                model.fieldValue.stringValue = data.idCardIssuedBy;

                return data.idCardIssuedBy;
            }
            return null;
        case 'Person_IdCardNumber_Form':
            // Полето трябва да е текстово. Събира инфомация за ЛК № на попълващия.
            if (data.idCardNumber && model.fieldType === 'Input' && (model.inputType === InputType.Text || model.inputType === InputType.TextArea)) {
                model.fieldValue.textValue = data.idCardNumber;
                model.fieldValue.stringValue = data.idCardNumber;

                return data.idCardNumber;
            }
            return null;
        case 'Person_Identification_Number':
            // Полето трябва да е текстово. Събира инфомация за ид.номенер на човека(ЕГН или ЛНЧ)
            if (data.personIdentification && data.personIdentification.number && model.fieldType === 'Input' && (model.inputType === InputType.Text || model.inputType === InputType.TextArea)) {
                model.fieldValue.textValue = data.personIdentification.number;
                model.fieldValue.stringValue = data.personIdentification.number;

                return data.personIdentification.number;
            }

            return null;
        case 'Person_Identification_Type':
            // Полето трябва да е dropdown от тип "Номенклатура"(Тип на документ за идентификация). Събира инфомация за тип на ид.номер(ЕГН или ЛНЧ).
            if (data.personIdentification && data.personIdentification.identificationTypeId && model.fieldType === 'Dropdown') {
                model.fieldValue.intValue = data.personIdentification.identificationTypeId;

                return data.personIdentification.personIdentification.identificationTypeId;
            }
            return null;
        case 'Person_InstitutionOther_Form':
            // Полето трябва да е текстово. Събира инфомация за алтернативната "Институция"(в свободен текст) на попълващия.
            if (data.institutionOther && model.fieldType === 'Input' && (model.inputType === InputType.Text || model.inputType === InputType.TextArea)) {
                model.fieldValue.textValue = data.institutionOther;
                model.fieldValue.stringValue = data.institutionOther;

                return data.institutionOther;
            }
            return null;
        case 'Person_InstitutionOtherCity_Form':
            // Полето трябва да е dropdown от тип "Номенклатура" с тип "Населени места". Събира инфомация за населеното място на алтернативната "Институция"(в свободен текст) на попълващия.
            if (data.institutionOtherCityId && model.fieldType === 'Dropdown') {
                model.fieldValue.intValue = data.institutionOtherCityId;

                return data.institutionOtherCityId
            }
            return null;
        case 'Person_InstitutionOtherCityStr_Form':
            // Полето трябва да е текстово. Събира информация за населеното място в свободен текст на алтернативната "Институция"(в свободен текст) на попълващия.
            if (data.institutionOtherCity && model.fieldType === 'Input' && (model.inputType === InputType.Text || model.inputType === InputType.TextArea)) {
                model.fieldValue.textValue = data.institutionOtherCity;
                model.fieldValue.stringValue = data.institutionOtherCity;

                return data.institutionOtherCity;
            }
            return null;
        case 'Person_InstitutionOtherCountry_Form':
            // Полето трябва да е dropdown от тип "Номенклатура". Събира инфомация за държавата на алтернативната "Институция"(в свободен текст) на попълващия.
            if (data.institutionOtherCountryId && model.fieldType === 'Dropdown') {
                model.fieldValue.intValue = data.institutionOtherCountryId;

                return data.institutionOtherCountryId;
            }
            return null;
        case 'Person_MobilePhone_Form':
            // Полето трябва да е текстово. Събира инфомация за моб.телефон на попълващия.
            if (data.mobilePhone && model.fieldType === 'Input' && (model.inputType === InputType.Text || model.inputType === InputType.TextArea)) {
                model.fieldValue.textValue = data.mobilePhone;
                model.fieldValue.stringValue = data.mobilePhone;

                return data.mobilePhone;
            }

            return null;
        case 'Person_Note_Form':
            // Полето трябва да е текстово. Събира инфомация за "Бележки" на попълващия.
            return null;
        case 'Person_Phone_Form':
            if (data.phone && model.fieldType === 'Input' && (model.inputType === InputType.Text || model.inputType === InputType.TextArea)) {
                model.fieldValue.textValue = data.phone;
                model.fieldValue.stringValue = data.phone;

                return data.phone;
            }

            return null;
        case 'Person_Position_Form':
            // Полето трябва да е dropdown от тип "Номенклатура". Събира инфомация за длъжността на попълващия.
            if (data.position && model.fieldType === 'Dropdown') {
                model.fieldValue.intValue = data.position;

                return data.position;
            }
            return null;
        case 'Person_PositionStatistic_Form':
            // Полето трябва да е dropdown от тип "Номенклатура". Събира инфомация за длъжността за стат.цели на попълващия.
            if (data.positionStatisticId && model.fieldType === 'Dropdown') {
                model.fieldValue.intValue = data.positionStatisticId;

                return data.positionStatisticId;
            }
            return null;
        case 'Person_PositionStr_Form':
            // Полето трябва да е dropdown от тип "Номенклатура". Събира инфомация за длъжността в свободен текст на попълващия.
            if (data.positionStr && model.fieldType === 'Input' && (model.inputType === InputType.Text || model.inputType === InputType.TextArea)) {
                model.fieldValue.textValue = data.positionStr;
                model.fieldValue.stringValue = data.positionStr;

                return data.positionStr;
            }
            return null;
        case 'Person_Salutation_Form':
            // Полето трябва да е dropdown от тип "Номенклатура". Събира инфомация за "Обръщение" на попълващия.
            if (data.salutationId && model.fieldType === 'Dropdown') {
                model.fieldValue.intValue = data.salutationId;

                return data.salutationId;
            }
            return null;
        case 'Person_TitleDegree_Form':
            // Полето трябва да е dropdown от тип "Номенклатура". Събира инфомация за "Звание/Степен" на попълващия.
            if (data.titleDegreeId && model.fieldType === 'Dropdown') {
                model.fieldValue.intValue = data.titleDegreeId;

                return data.titleDegreeId;
            }
            return null;
        case 'Person_BankName_Form':
            // Полето трябва да е текстово. Събира инфомация за името на банката на банковите данни на попълващия.
            if (data.bankAccount && data.bankAccount.bankName && model.fieldType === 'Input' && (model.inputType === InputType.Text || model.inputType === InputType.TextArea)) {
                model.fieldValue.textValue = data.bankAccount.bankName;
                model.fieldValue.stringValue = data.bankAccount.bankName;

                return data.bankAccount.bankName;
            }

            return null;
        case 'Person_BankBic_Form':
            // Полето трябва да е текстово. Събира инфомация за BIC на банковите данни на попълващия.
            if (data.bankAccount && data.bankAccount.bic && model.fieldType === 'Input' && (model.inputType === InputType.Text || model.inputType === InputType.TextArea)) {
                model.fieldValue.textValue = data.bankAccount.bic;
                model.fieldValue.stringValue = data.bankAccount.bic;

                return data.bankAccount.bic;
            }

            return null;
        case 'Person_BankIban_Form':
            // Полето трябва да е текстово. Събира инфомация за IBAN на банковите данни на попълващия.
            if (data.bankAccount && data.bankAccount.iban && model.fieldType === 'Input' && (model.inputType === InputType.Text || model.inputType === InputType.TextArea)) {
                model.fieldValue.textValue = data.bankAccount.iban;
                model.fieldValue.stringValue = data.bankAccount.iban;

                return data.bankAccount.iban;
            }

            return null;
        case 'Person_BankAccountDescription_Form':
            // Полето трябва да е текстово. Събира инфомация за детайли на банковите данни на попълващия.
            if (data.bankAccount && data.bankAccount.description && model.fieldType === 'Input' && (model.inputType === InputType.Text || model.inputType === InputType.TextArea)) {
                model.fieldValue.textValue = data.bankAccount.description;
                model.fieldValue.stringValue = data.bankAccount.description;

                return data.bankAccount.description;
            }

            return null;
        case 'Institution_of_employment_form':
            // Полето трябва да е dropdown от тип "Регистър" с тип "Institution". Събира инфомация за "Институция" на попълващия.

            if (data.institutionRegisterId && model.fieldType === 'Dropdown') {
                model.fieldValue.intValue = data.institutionRegisterId;

                return data.institutionRegisterId;
            }

            return null;
        default:
            return null;
    }
}


export default {
    errorStatusCode,
    errorText,
    errorColor,
    notificationColor,
    kendoDataSourceErrorText,
    responseMessage,
    castIdRouteParams,
    castRelatedIdRouteParams,
    responseStatusCode,
    convertFromUtcToLocal,
    updateFieldTemplateObject,
    isNumeric,
    getBoolean,
    getMassFilterForGrid,
    dateRangesDropdownSource,
    calculateDateRange,
    haveClaimReadRights,
    haveClaimCreateOrEditRights,
    haveClaimDeleteRights,
    notify,
    roundToSecondDigit,
    getGridSelectedRows,
    getGridSelectedRowsIds,
    getGridSelectedRowsByProperty,
    getUid,
    toBase64,
    sizeClassObject,
    translateError,
    gridTextCellTemplate,
    toggleEllpsisClass,
    filter,
    isIsoDate,
    isInt,
    isFloat,
    blobToFile,
    kendoVirtualizationValueMapper,
    fillProfileDataToDynamicForm,
}
